import React, { useState, useMemo, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { togglePropertyFavorite } from 'data/reducers/propertyManagementReducer';
import './PropertyFavoriteIcon.css';

const PropertyFavoriteIcon = ({
	property,
	currentUser,
	isFavorite,
	size = 'medium',
	onToggleFavorite,
	className = '',
	showTooltip = true,
	disabled = false,
}) => {
	const dispatch = useDispatch();

	// Get favorite properties from Redux
	const { favoriteProperties = [] } = useSelector(
		(state) => state.propertyManagement || {}
	);

	// Local loading state
	const [isToggling, setIsToggling] = useState(false);

	// Helper function to check if property is in favorites - memoized
	const isPropertyInFavorites = useCallback(
		(propertyId) => {
			return favoriteProperties.some(
				(favProperty) => String(favProperty.id) === String(propertyId)
			);
		},
		[favoriteProperties]
	);

	// Determine if the property is actually favorited - prioritize Redux state - memoized
	const isActuallyFavorite = useMemo(
		() => isPropertyInFavorites(property.id) || false,
		[isPropertyInFavorites, property.id]
	);

	const handleFavoriteClick = useCallback(
		async (e) => {
			e?.preventDefault();
			e?.stopPropagation();

			if (disabled || isToggling) return;

			setIsToggling(true);

			try {
				if (onToggleFavorite) {
					// Use custom toggle function if provided
					await onToggleFavorite(property, isActuallyFavorite);
				} else {
					// Default: use Redux action to toggle favorite
					await dispatch(
						togglePropertyFavorite(property.id, isActuallyFavorite)
					);
				}
			} catch (error) {
				console.error('Error toggling property favorite:', error);
			} finally {
				setIsToggling(false);
			}
		},
		[
			disabled,
			isToggling,
			onToggleFavorite,
			property,
			isActuallyFavorite,
			dispatch,
		]
	);

	// Size mapping for different use cases - memoized
	const sizeClasses = useMemo(
		() => ({
			small: 'property-favorite-icon-small',
			medium: 'property-favorite-icon-medium',
			large: 'property-favorite-icon-large',
		}),
		[]
	);

	const sizeClass = useMemo(
		() => sizeClasses[size] || sizeClasses.medium,
		[sizeClasses, size]
	);

	// Memoized className computation
	const containerClassName = useMemo(
		() =>
			`property-favorite-icon ${sizeClass} ${
				isActuallyFavorite ? 'active' : ''
			} ${disabled || isToggling ? 'disabled' : ''} ${className}`,
		[sizeClass, isActuallyFavorite, disabled, isToggling, className]
	);

	// Memoized title computation
	const titleText = useMemo(() => {
		if (!showTooltip) return undefined;
		if (isToggling) return 'Updating...';
		return isActuallyFavorite ? 'Remove from favorites' : 'Add to favorites';
	}, [showTooltip, isToggling, isActuallyFavorite]);

	// Memoized tabIndex computation
	const tabIndexValue = useMemo(
		() => (disabled || isToggling ? -1 : 0),
		[disabled, isToggling]
	);

	// Memoized keydown handler
	const handleKeyDown = useCallback(
		(e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				handleFavoriteClick(e);
			}
		},
		[handleFavoriteClick]
	);

	// Memoized star icon className
	const starIconClassName = useMemo(
		() => `bi bi-star${isActuallyFavorite ? '-fill' : ''}`,
		[isActuallyFavorite]
	);

	// Memoized spinner style
	const spinnerStyle = useMemo(
		() => ({ animation: 'spin 1s linear infinite' }),
		[]
	);

	return (
		<div
			key={property.id}
			className={containerClassName}
			onClick={handleFavoriteClick}
			title={titleText}
			role="button"
			tabIndex={tabIndexValue}
			onKeyDown={handleKeyDown}
		>
			{isToggling ? (
				<i className="bi bi-arrow-repeat" style={spinnerStyle} />
			) : (
				<i className={starIconClassName} />
			)}
		</div>
	);
};

PropertyFavoriteIcon.propTypes = {
	property: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		is_favorite: PropTypes.bool,
		isFavorite: PropTypes.bool,
	}).isRequired,
	currentUser: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		email: PropTypes.string,
	}),
	isFavorite: PropTypes.bool,
	size: PropTypes.oneOf(['small', 'medium', 'large']),
	onToggleFavorite: PropTypes.func,
	className: PropTypes.string,
	showTooltip: PropTypes.bool,
	disabled: PropTypes.bool,
};

export default memo(PropertyFavoriteIcon);
